import { PaywallCustomization } from "@circle-react/components/Paywalls/Checkout/CheckoutPage/CheckoutDescription/PaywallCustomization";
import { OfferPricePreviewContext } from "@circle-react/components/Paywalls/Checkout/CheckoutPage/CheckoutForm/PricePreview/PricePreviewProvider/OfferPricePreviewContext";
import { useCheckoutSelectedPrice } from "@circle-react/components/Paywalls/Checkout/hooks/useCheckoutSelectedPrice";
import { useCouponPreviewMutation } from "@circle-react/components/Paywalls/Checkout/hooks/useCouponPreviewMutation";
import { usePreviewMutation } from "@circle-react/components/Paywalls/Checkout/hooks/usePreviewMutation";
import type {
  Paywall,
  PaywallCustomization as PaywallCustomizationData,
} from "@circle-react/types";
import { noop } from "lodash";
import { createContext, useContext, useCallback, useEffect } from "react";
import type { PropsWithChildren } from "react";
import { useFormContext } from "react-hook-form";
import invariant from "tiny-invariant";

import { isCouponApplied } from "../../../Checkout/hooks/useGetCouponApplication";
import type { CheckoutPreviewContext } from "../../../Checkout/types/CheckoutProps";
import { useButtonActions } from "./hooks/useButtonActions";

interface OfferPageProviderProps {
  couponPreviewMutation: ReturnType<typeof useCouponPreviewMutation>;
  getSelectedPrice: ReturnType<
    typeof useCheckoutSelectedPrice
  >["getSelectedPrice"];
  hasAlreadyPurchased: boolean;
  isPreviewing: boolean;
  isSelectedPrice: ReturnType<
    typeof useCheckoutSelectedPrice
  >["isSelectedPrice"];
  isUpgrade: boolean;
  paywall: Paywall;
  previewMutation: ReturnType<typeof usePreviewMutation>;
  refuseOfferMutation: ReturnType<typeof useButtonActions>["refuseMutation"];
  setSelectedPrice: ReturnType<
    typeof useCheckoutSelectedPrice
  >["setSelectedPrice"];
  stepId: number;
}

const OfferPageProvider = createContext<OfferPageProviderProps | null>(null);

OfferPageProvider.displayName = "OfferPageProvider";

export const useOfferPageContext = () => {
  const context = useContext(OfferPageProvider);

  invariant(
    context,
    "useOfferPageContext must be used within a OfferPageContext.Provider",
  );

  return context;
};

interface OfferPageContextProps {
  checkoutPreviewContext?: CheckoutPreviewContext;
  isPreviewing?: boolean;
  isUpgrade?: boolean;
  paywall: Paywall;
  paywallCustomization?: PaywallCustomizationData;
  stepId: string | number;
}

export const OfferPageContextProvider = ({
  children,
  paywall,
  paywallCustomization,
  stepId,
  isPreviewing = false,
  checkoutPreviewContext,
  isUpgrade = false,
}: PropsWithChildren<OfferPageContextProps>) => {
  const previewMutation = usePreviewMutation({
    checkoutPreviewContext,
    paywall,
  });
  const couponPreviewMutation = useCouponPreviewMutation({
    checkoutPreviewContext,
    paywall,
  });
  const preview = previewMutation.mutate;
  const previewCoupon = couponPreviewMutation.mutate;

  const { watch, setValue } = useFormContext();
  const { paywall_price_id, coupon_code } = watch();

  const { getSelectedPrice, setSelectedPrice, isSelectedPrice } =
    useCheckoutSelectedPrice(paywall, noop);

  const setSelectedPriceForm = (value: any) => {
    setSelectedPrice(value);
    setValue("paywall_price_id", value);
  };

  const { refuseMutation } = useButtonActions({
    stepId: Number(stepId),
  });

  const values = {
    stepId: Number(stepId),
    paywall, // Needed for PricePreviewContext
    previewMutation, // Needed for PricePreviewContext
    couponPreviewMutation,
    getSelectedPrice,
    setSelectedPrice: setSelectedPriceForm,
    isSelectedPrice,
    isPreviewing,
    hasAlreadyPurchased: false, // Needed for PricePreviewContext
    isUpgrade,
    refuseOfferMutation: refuseMutation,
  };

  const previewCheckout = useCallback(() => {
    if (isUpgrade) return;

    if (
      ["", null, undefined].includes(coupon_code) ||
      (coupon_code && couponPreviewMutation.isSuccess)
    ) {
      const prices = couponPreviewMutation.data?.paywall?.prices || [];
      const params = {
        paywall_price_id,
        coupon_code: isCouponApplied(paywall_price_id, prices)
          ? coupon_code
          : "",
      };

      preview({ queryParams: params });
    }
  }, [
    couponPreviewMutation.isSuccess,
    couponPreviewMutation.data,
    coupon_code,
    isUpgrade,
    paywall_price_id,
    preview,
  ]);

  useEffect(() => {
    if (isUpgrade) return;
    previewCheckout();
  }, [
    isUpgrade,
    paywall_price_id,
    couponPreviewMutation.status,
    previewCheckout,
  ]);

  useEffect(() => {
    if (isUpgrade || ["", null, undefined].includes(coupon_code)) {
      return;
    }

    previewCoupon({
      paywall_id: paywall.id,
      coupon_code,
      scenario: "apply_coupon",
    });
  }, [coupon_code, isUpgrade, paywall.id, previewCoupon]);

  return (
    <OfferPageProvider.Provider value={values}>
      <OfferPricePreviewContext>
        <PaywallCustomization.Provider
          paywallCustomization={paywallCustomization}
        >
          {children}
        </PaywallCustomization.Provider>
      </OfferPricePreviewContext>
    </OfferPageProvider.Provider>
  );
};
